import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarNav, CalendarNext, CalendarPrev, CalendarToday, Eventcalendar, localeFr, MbscCalendarEvent, MbscCalendarEventData, MbscResource, setOptions } from '@mobiscroll/react'
import { LocalDate } from '../../../../utils/date/local-date'
import { DateFormat, DateUnit, LocalizedDateFormat, TimeFormat } from '../../../../utils/date/model/date'
import '../style.css'
import { MbscEventcalendarView, MbscEventClickEvent } from '@mobiscroll/react/dist/src/core/components/eventcalendar/eventcalendar.types.public'
import { compose } from 'redux'
import { Grid, withStyles } from '@material-ui/core'
import { getStyles } from 'isotope-client'
import { capitalizeFirstLetter, formatMinutesToHHMM } from '../../../../utils/textUtils'
import Loader from '../../../../components/layout/Loader'
import { Ressource, ScheduledTask, TaskPanelRedirectionParameters } from '../services/schedule.types'
import { useGestionTacheContext } from '../../gestionTache/GestionTacheProvider'
import { InvalidPeriodType, SchedulerViewMode } from '../services/schedule.enums'
import { useIntl } from 'react-intl'
import { LocalDateTime } from '../../../../utils/date/local-date-time'
import Switch from '../../../../components/Switch'
import { ELEMENTS_TO_DISPLAY, MAX_TIME, MIN_TIME } from '../services/schedule.consts'
import TaskCard from './planning/TaskCard'
import { getWeatherIcon } from '../../tourPlaine/component/contextePedoclimatique/tool/contextePedoclimatique.utils'
import { DEFAULT_TIMEZONE } from '../../../../utils/constants'
import { useScheduler } from '../context/SchedulerContext'
import { ReactComponent as BasketFill } from '../../../../icons/basket-fill.svg'
import { ReactComponent as BasketUnfill } from '../../../../icons/basket-unfill.svg'
import BlockIcon from '@material-ui/icons/Block'
import { buildTaskParameters, getCapacityDurationForDay, getCapacityDurationForDayAndRessource, getWorkingDurationForDay, getWorkingDurationForDayAndRessource } from '../services/schedule.utils'

const customLocale = {
	...localeFr,
	todayText: 'Voir aujourd\'hui',
}

setOptions({
	locale: customLocale,
	theme: 'material',
	themeVariant: 'light'
})

const VIEW_MODE_OPTIONS = Object.values(SchedulerViewMode).map(mode => ({
	id: mode,
	label: `enums.schedulerViewMode.${mode}`
}))

const CALENDAR_TIME_STEP = 5

/**
 * Styles
 */
const styles = () => getStyles({
	loaderContainer: {
		display: 'flex',
		flex: 1,
		height: '100%',
		justifyContent: 'center',
		alignItems: 'center'
	}
})

type SchedulerTasksContainerProps = {
	elementsTypesToDisplay?: string,
	classes: ClassNameMap,
	setTaskPanelRedirectionParameters: Dispatch<SetStateAction<TaskPanelRedirectionParameters | undefined>>
}

const SchedulerTasksContainer: FC<SchedulerTasksContainerProps> = ({ elementsTypesToDisplay = ELEMENTS_TO_DISPLAY.RESSOURCES, classes, setTaskPanelRedirectionParameters }) => {
	const {
		viewInfos: {
			horairesFerme,
			periodePlanification,
			ressources,
			tools,
			contextesPedoclimatiques,
			periodesInvalides,
			pauseRepas,
			capacitePlanification
		},
		tasks,
		isLoading,
		nbDays,
		selectedDate,
		selectedRessources,
		viewMode,
		changeView,
		handleFilters
	} = useScheduler()
	const { isResultat } = useGestionTacheContext()
	const { formatMessage } = useIntl()
	const [targetedViewTime, setTargetedViewTime] = useState<string>('')
	const [elementsToDisplay, setElementsToDisplay] = useState<Ressource[]>(ressources)

	/**
	 * useMemo
	 */
	const viewInfo = useMemo<MbscEventcalendarView>(
		() => {
			// Filtrage sur 7j = Semaine actuelle
			return ({
				schedule: {
					type: nbDays === 7 ? 'week' : 'day',
					allDay: false,
					timeCellStep: CALENDAR_TIME_STEP,
					timeLabelStep: CALENDAR_TIME_STEP,
					currentTimeIndicator: true,
					size: nbDays === 7 ? 1 : nbDays
				}
			})
		},
		[nbDays]
	)

	// Heure à laquelle nous voulons scroller sur le calendrier selon le mode
	// Workaround pour forcer le rerendu et revenir à la date initiale si on a scrollé sur la vue
	useEffect(() => {
		const viewPeriodMin = nbDays === 7 ? selectedDate.startOf(DateUnit.week) : selectedDate
		const viewPeriodMax = nbDays === 1 ? selectedDate : selectedDate.plus(nbDays - 1, DateUnit.day)
		let startTime = horairesFerme.start

		// On sélection le time initial selon le mode choisi
		// Horaire ferme = début de la ferme
		// Centré sur tâche = heure min de début de tâche
		if (viewMode === SchedulerViewMode.TASK_INTERVAL && tasks.length > 0) {
			// On filtre la liste des tâches pour n'avoir que celles dans l'intervalle de date affiché et avec les ressources affichées
			const filteredTasks = tasks
				.filter(task =>
					LocalDateTime.fromAPIKeepLocalTime(task.start)._compareTo(viewPeriodMin.atTimeString(MIN_TIME)) >= 0 && LocalDateTime.fromAPIKeepLocalTime(task.end)._compareTo(viewPeriodMax.atTimeString(MAX_TIME)) <= 0
					&& selectedRessources.filter(sR => task.resource.includes(sR.code)).length > 0
				)

			if (filteredTasks.length) {
				// On parcourt les tâches et récupère l'heure min
				const minTask = filteredTasks.reduce((min, item) => LocalDateTime.fromAPIKeepLocalTime(item.start).time < LocalDateTime.fromAPIKeepLocalTime(min.start).time ? item : min, tasks[0])

				startTime = LocalDateTime.fromAPIKeepLocalTime(minTask.start).time.format(TimeFormat.TIME_24_WITH_SECONDS)
			}
		}

		setTargetedViewTime(prevState => {
			// Pour forcer un nouveau zoom alors que l'on a déjà la même heure en initial, on change les secondes
			// Aucun impact UI car non géré par mobiscroll mais permet de rerendre la vue
			if (startTime === prevState) {
				return `${startTime.substr(0,5)}:10`
			}
			return startTime
		})
	}, [horairesFerme, nbDays, viewMode, selectedDate, tasks, selectedRessources])

	// utilisé pour l'onglet Debug (cf DebugOnglet.jsx)
	useEffect(() => {
		if (elementsTypesToDisplay === ELEMENTS_TO_DISPLAY.TOOLS) {
			setElementsToDisplay(tools)
		}
	}, [tools])

	const filteredRessources = useMemo(() => {
		return elementsToDisplay
			.filter((r) => selectedRessources.find((sr) => sr.code === r.id))
			.map(r => ({
				...r,
				cssClass: 'resource-column-min'
			}))
			.sort((r1, r2) => {
				// Tri des ressources dans le calendrier selon la sélection dans le picker
				const i1 = selectedRessources.findIndex((sr) => sr.code === r1.id)
				const i2 = selectedRessources.findIndex((sr) => sr.code === r2.id)

				return i1 - i2
			})

	}, [elementsToDisplay, selectedRessources])

	const calendarData = useMemo(() => [...tasks], [tasks])

	const inv = useMemo(() => periodesInvalides.map(periode => {
		switch (periode.type) {
			case InvalidPeriodType.FarmClosed:
				return {
					start: periode.start,
					end: periode.end,
					title: formatMessage({ id: `enums.invalidPeriodType.${InvalidPeriodType.FarmClosed}` }),
					cssClass: 'md-closed-class mbsc-flex'
				}
			case InvalidPeriodType.NotWorking:
				return {
					start: periode.start,
					end: periode.end,
					title: formatMessage({ id: `enums.invalidPeriodType.${InvalidPeriodType.NotWorking}` }),
					resource: periode.idRessource,
					cssClass: 'md-timeout-class mbsc-flex'
				}
			default:
				return {}
		}
	}), [periodesInvalides])

	const colors = useMemo(() => ([{
		start: pauseRepas.start,
		end: pauseRepas.end,
		title: formatMessage({ id: `enums.invalidPeriodType.${InvalidPeriodType.LunchBreak}` }),
		recurring: {
			repeat: 'daily' as 'daily'
		},
		cssClass: 'md-lunch-break-class mbsc-flex'
	}]), [pauseRepas])

	const handleChange = useCallback((args) => {
		const date = args.date
		if (date) {
			handleFilters({
				selectedDate: LocalDate.fromJSWithTimezone(date, DEFAULT_TIMEZONE)
			})
		}
	}, [handleFilters])

	const showDetail = useCallback((args: MbscEventClickEvent) => {
			const event = args.event

			setTaskPanelRedirectionParameters({
				id: `${event.id}`,
				unplanned: false,
				taskParameters: buildTaskParameters(event, event.resources, elementsToDisplay)
			})
		},
		[setTaskPanelRedirectionParameters]
	)

	const editTask = useCallback((fromUnscheduled: boolean, updatedEvent: MbscCalendarEvent, oldEvent?: MbscCalendarEvent) => {
		const oldStart = oldEvent?.start ? LocalDateTime.fromJS(new Date(oldEvent?.start as string)).format() : undefined
		const newStart = LocalDateTime.fromJS(new Date(updatedEvent.start as string)).format()
		const oldEnd = oldEvent?.end ? LocalDateTime.fromJS(new Date(oldEvent?.end as string)).format() : undefined
		const newEnd = LocalDateTime.fromJS(new Date(updatedEvent.end as string)).format()

		// On ouvre le panel d'édition si l'assignation ou la date ont changé
		if (!oldEvent || oldStart !== newStart || oldEnd !== newEnd || oldEvent?.resource !== updatedEvent.resource) {
			// On conserve une éventuelle ancienne ressource si ça vient du unscheduled container
			const oldRessources = (oldEvent?.resources && fromUnscheduled) ? (Array.isArray(oldEvent.resources) ? oldEvent.resources : [oldEvent.resources]) as string[] : []

			let newRessources: string[] = []
			if (selectedRessources.length === 1) {
				newRessources = selectedRessources.map(s => s.code)
			} else {
				newRessources = (Array.isArray(updatedEvent.resource) ? updatedEvent.resource : [updatedEvent.resource]) as string[]
			}
			const ressourcesSent = [...oldRessources, ...newRessources]

			setTaskPanelRedirectionParameters({
				id: `${updatedEvent.id}`,
				unplanned: false,
				forEdition: true,
				taskParameters: buildTaskParameters(
					updatedEvent,
					ressourcesSent.filter((item, index) => ressourcesSent.indexOf(item) === index),
					elementsToDisplay
				)
			})
		}
	}, [setTaskPanelRedirectionParameters, selectedRessources])

	/**
	 * Affiche le différentiel de temps pour les jours/ressources
	 */
	const renderDiffTime = (capacity: number, working: number) => {
		const diff = capacity - working
		const isPositive = diff >= 0
		const iconClassName = `time-icon ${isPositive ? 'time-icon-valid' : 'time-icon-invalid'}`
		const labelClassName = `time-label ${isPositive ? 'time-label-valid' : 'time-label-invalid'}`

		return <div className="time-container">
			{isPositive ? <BasketFill className={iconClassName} /> : <BasketUnfill className={iconClassName} />}
			<span className={labelClassName}>{formatMinutesToHHMM(Math.abs(diff))}</span>
		</div>
	}

	/**
	 * Affiche le temps restant pour une journée
	 */
	const renderDayTime = useCallback((date: LocalDate, onlyOneDay: boolean) => {
		return <div className={onlyOneDay ? 'time-one-day' : ''}>
			{renderDiffTime(
				getCapacityDurationForDay(capacitePlanification, date),
				getWorkingDurationForDay(calendarData, date)
			)}
		</div>
	}, [capacitePlanification, calendarData])

	/**
	 * Affiche la météo pour une journée
	 * Affichage 1/2 pictos selon si la météo a été planifiée à la journée ou à la demi-journée
	 */
	const renderWeather = useCallback((date: LocalDate, onlyOneDay: boolean) => {
		const dayAndMonth = date.format(LocalizedDateFormat.DAY_AND_MONTH)
		const contexte = contextesPedoclimatiques.find(c =>
			LocalDate.fromAPI(c.date).format(LocalizedDateFormat.DAY_AND_MONTH) === dayAndMonth
		)
		const sameMeteoWholeDay = contexte?.matin.exterieur?.meteo === contexte?.apresMidi.exterieur?.meteo

		return <div className={`header-template-weather ${onlyOneDay ? 'weather-one-day' : ''}`}>
			{contexte?.matin.exterieur?.meteo ?
				<>
					<div>{getWeatherIcon(contexte?.matin.exterieur?.meteo.toLowerCase(), 20)}</div>

					{!sameMeteoWholeDay && <div>{getWeatherIcon(contexte?.apresMidi.exterieur?.meteo.toLowerCase(), 20)}</div>}
				</>
				:
				<>
					<div><BlockIcon className="header-template-weather-unknown" /></div>
				</>
			}
		</div>
	}, [contextesPedoclimatiques])

	/**
	 * Affiche l'en-tête jour de mobiscroll
	 * Utilisé uniquement pour l'affichage de plusieurs jours
	 */
	const renderCustomDay = useCallback((args: { date: Date }) => {
		const date = args.date
		const localDate = LocalDate.fromJSWithTimezone(date, DEFAULT_TIMEZONE)
		const weekday = LocalDate.fromJSWithTimezone(date, DEFAULT_TIMEZONE).format(LocalizedDateFormat.WEEKDAY_ONLY)
		const dayAndMonth = LocalDate.fromJSWithTimezone(date, DEFAULT_TIMEZONE).format(LocalizedDateFormat.DAY_AND_MONTH)

		return (
			<div className="header-template-container">
				<div className="header-template-date">
					<div className="header-template-day-name">{capitalizeFirstLetter(weekday)}</div>
					<div className="header-template-day">{capitalizeFirstLetter(dayAndMonth)}</div>
				</div>
				<div className="header-template-second-row">
					{renderDayTime(localDate, false)}
					{renderWeather(localDate, false)}
				</div>
			</div>
		)
	}, [renderWeather])

	/**
	 * Affiche l'en-tête ressource de mobiscroll
	 */
	const renderCustomResource = useCallback((resource: MbscResource, day: Date) => {
		const localDate = day ? LocalDate.fromJSWithTimezone(day, DEFAULT_TIMEZONE) : selectedDate
		let capacityDuration = 0
		let workingDuration = 0

		// Cas spécifique si on affiche qu'une seule ressource sur plusieurs jours
		if (selectedRessources.length === 1) {
			const startDate = nbDays === 7 ? selectedDate.startOf(DateUnit.week) : selectedDate

			for (let dayIndex = 0; dayIndex < nbDays; dayIndex++) {
				const weekDay = startDate.plus(dayIndex)
				capacityDuration += getCapacityDurationForDayAndRessource(capacitePlanification, weekDay, `${resource.id}`)
				workingDuration += getWorkingDurationForDayAndRessource(calendarData, weekDay, `${resource.id}`)
			}
		} else {
			capacityDuration = getCapacityDurationForDayAndRessource(capacitePlanification, localDate, `${resource.id}`)
			workingDuration = getWorkingDurationForDayAndRessource(calendarData, localDate, `${resource.id}`)
		}

		return (
			<div className="resource-container">
				{/*note: lorsqu'on affiche les outils dans l'onglet debug, l'alias correspond au stock de l'outil*/}
				<div className="resource-name">{resource.name} ({resource.alias})</div>
				<div>
					{renderDiffTime(capacityDuration, workingDuration)}
				</div>
			</div>
		)
	}, [selectedDate, selectedRessources, nbDays, capacitePlanification, calendarData])

	/**
	 * Affiche l'en-tête filtre de mobiscroll
	 */
	const renderCustomHeader = useCallback(() => {
		return <Grid container>
			{nbDays === 1 && renderDayTime(selectedDate, true)}
			<Grid item xs={2}>
				<CalendarNav />
			</Grid>
			<Grid item container xs={10} justify="flex-end">
				<CalendarToday />
				<CalendarPrev />
				<CalendarNext />
				<Switch
					items={VIEW_MODE_OPTIONS}
					setValue={(item) => changeView(item.id)}
					defaultValue={VIEW_MODE_OPTIONS[0]}
					additionalStyle={{ width: 130, padding: 0 }}
				/>
			</Grid>
			{nbDays === 1 && renderWeather(selectedDate, true)}
		</Grid>
	}, [selectedDate, renderWeather])

	const renderScheduleEvent = useCallback((data: MbscCalendarEventData) => {
		return <TaskCard task={data.original as ScheduledTask} id={data.id} />
	}, [])

	if (isLoading) {
		return (
			<Loader fromBo={false} minHeight={false} style={classes.loaderContainer} />
		)
	}

	return (
		<>
			<Eventcalendar
				data={calendarData}
				resources={filteredRessources}
				view={viewInfo}
				groupBy="date"
				showEventTooltip={false}
				clickToCreate={false}
				dragToCreate={false}
				dragToResize={false}
				dragToMove={!isResultat}
				externalDrop={!isResultat}
				externalDrag={!isResultat}
				dragTimeStep={CALENDAR_TIME_STEP}
				onEventClick={showDetail}
				onEventCreated={(args) => editTask(true, args.event)}
				onEventUpdated={(args) => editTask(false, args.event, args.oldEvent)}
				invalid={inv}
				colors={colors}
				refDate={selectedDate.format(DateFormat.SHORT_DATE_WITH_DASH)}
				selectedDate={`${selectedDate.format(DateFormat.SHORT_DATE_WITH_DASH)} ${targetedViewTime}`}
				onSelectedDateChange={handleChange}
				min={isResultat ? LocalDateTime.fromAPIKeepLocalTime(periodePlanification.start).startOfDay().toJSON() : undefined}
				max={isResultat ? LocalDateTime.fromAPIKeepLocalTime(periodePlanification.end).plus(-1).endOfDay().toJSON() : undefined}
				firstDay={1}
				renderHeader={renderCustomHeader}
				renderDay={renderCustomDay}
				renderResource={renderCustomResource}
				renderScheduleEvent={renderScheduleEvent}
				timeFormat="HH:mm"
			/>
		</>
	)
}

type SchedulerTasksContainerParentProps = {
	elementsTypesToDisplay: string
	setTaskPanelRedirectionParameters: Dispatch<SetStateAction<TaskPanelRedirectionParameters | undefined>>
}

export default compose<FC<SchedulerTasksContainerParentProps>>(
	withStyles(styles)
)(SchedulerTasksContainer)

